import { useEffect, useState, useCallback, useRef, useMemo } from "react";

import { useAnalyticsEvents } from "@atlaskit/analytics-next";

import ErrorBoundary from "@/components/error-boundary";
import { ufoEventRedirectToProduct } from "@/components/signup/utils";
import { trackPerceivedProvisioningEvent } from "@/templates/ondemand/signup/confirmation/confirmation-page/src/utils/analytics";
import {
    getExperimentWithExposures,
    experiments,
    ExperimentWithoutExposureEnrollmentStatus
} from "@/utils/experiment";

import {
    checkFeatureFlagGate,
    featureFlags
} from "../../../../../../../../utils/feature-flags";
import {
    useCloudStatusRedirection,
    useCloudStatus,
    useTenantAwareStatsigClientUpdater,
    useSearchParameters,
    useCreateSampleProject
} from "../../hooks";
import { JiraAnimatedProvisioningScreen } from "../jira-animated-provisioning-screen/jira-animated-provisioning-screen";

import { ANALYTICS_PREFIX } from "./common/constants";
import {
    useEligibleForSampleProject,
    useShowOnboardingWizardScreen,
    useShowOnboardingWizardScreenForKanban,
    useShowOnboardingWizardScreenForRouteBiz,
    useShowOnboardingWizardScreenForScrum
} from "./controllers/index";
import { useCreateRedirectUrlWithWizard } from "./utils/create-redirect-url";
import { WelcomeModal } from "./wizard/welcome-modal";

type ExperimentKeys = keyof typeof experiments;

export const JiraProvisioningWithOnboardingWizard = () => {
    const { createAnalyticsEvent } = useAnalyticsEvents();
    const createRedirectUrlWithWizard = useCreateRedirectUrlWithWizard();

    const {
        createCloudStatus,
        startPerceivedProvisioningTimer,
        perceivedProvisioningStartTime
    } = useCloudStatus();
    const {
        setWaitingForProvisioning,
        redirectUrl,
        addBlockingRedirectPromise,
        resolveBlockingRedirectPromises,
        createRedirectUrl
    } = useCloudStatusRedirection();
    const { products, signupId } = useSearchParameters();
    const productKey = products.length > 0 ? products[0] : undefined;
    const provisioningEventFired = useRef(false);

    const [siteProvisionedOnMount] = useState(createCloudStatus === "success");

    const { userEligibleForWizardExperiment } = useShowOnboardingWizardScreen();
    const { userEligibleForWizardForScrumExperiment } =
        useShowOnboardingWizardScreenForScrum();
    const { userEligibleForWizardForDevKanbanExperiment } =
        useShowOnboardingWizardScreenForKanban();
    const { userEligibleForWizardForRouteBizExperiment } =
        useShowOnboardingWizardScreenForRouteBiz();
    const { userEligibleForSampleProject } = useEligibleForSampleProject();
    const { updatedStatsigClientWithCloudId } =
        useTenantAwareStatsigClientUpdater();

    const createSampleProjectRequestInitialized = useRef(false);
    const { createSampleProject } = useCreateSampleProject();

    const isSampleProjectVariation = useMemo(() => {
        if (!userEligibleForSampleProject) {
            return false;
        }

        const sampleProjectExperiment = getExperimentWithExposures(
            experiments.jfindMatureExampleProjectExperiment
        );

        return (
            sampleProjectExperiment.type === "enrolled" &&
            sampleProjectExperiment.value
        );
    }, [userEligibleForSampleProject]);

    useEffect(() => {
        if (
            isSampleProjectVariation &&
            createCloudStatus === "success" &&
            createSampleProjectRequestInitialized.current === false
        ) {
            createSampleProjectRequestInitialized.current = true;
            const sampleProjectPromise = createSampleProject();
            addBlockingRedirectPromise(sampleProjectPromise);
        }
    }, [
        isSampleProjectVariation,
        userEligibleForSampleProject,
        createCloudStatus,
        addBlockingRedirectPromise,
        createSampleProject
    ]);

    useEffect(() => {
        setWaitingForProvisioning(true);
    }, [setWaitingForProvisioning]);

    useEffect(() => {
        startPerceivedProvisioningTimer();
    }, [startPerceivedProvisioningTimer]);

    const isEnrolledAndEnabled = (
        experiment: ExperimentWithoutExposureEnrollmentStatus<unknown>
    ) => {
        return experiment.type === "enrolled" && experiment.value;
    };
    const checkExperimentEligibility = (
        isEligible: boolean,
        experimentKey: ExperimentKeys
    ) => {
        if (!isEligible) return false;
        const experiment = getExperimentWithExposures(
            experiments[experimentKey]
        );
        return isEnrolledAndEnabled(experiment);
    };
    const trackProvisioningEventIfNeeded = (isWizardVariation: boolean) => {
        if (isWizardVariation && !provisioningEventFired.current) {
            trackPerceivedProvisioningEvent({
                createAnalyticsEvent,
                productKey,
                finalProvisioningScreen: "jira-onboarding-wizard",
                startTimeSeconds: perceivedProvisioningStartTime,
                signupId
            });
            provisioningEventFired.current = true;
        }
    };
    const getIsWizardVariation = () => {
        if (!updatedStatsigClientWithCloudId) {
            return { isWizardVariation: false, withSampleData: false };
        }
        const experimentChecks: {
            isEligible: boolean;
            experimentKey: ExperimentKeys;
        }[] = [
            {
                isEligible: userEligibleForWizardForRouteBizExperiment,
                experimentKey: "jiraProjectOnboardingWizardForRouteBiz"
            },
            {
                isEligible: userEligibleForWizardForScrumExperiment,
                experimentKey: "jiraProjectOnboardingWizardForScrum"
            },
            {
                isEligible: userEligibleForWizardExperiment,
                experimentKey: "jiraProjectOnboardingWizardTenant"
            },
            {
                isEligible: userEligibleForWizardForDevKanbanExperiment,
                experimentKey: "jiraProjectOnboardingWizardDevKanban"
            }
        ];
        let isWizardVariation = false;
        let withSampleData = false;
        experimentChecks.forEach(({ isEligible, experimentKey }) => {
            if (checkExperimentEligibility(isEligible, experimentKey)) {
                isWizardVariation = true;

                if (experimentKey === "jiraProjectOnboardingWizardForScrum") {
                    if (
                        checkExperimentEligibility(
                            true,
                            "jiraProjectOnboardingWizardForScrumWithSampleData"
                        )
                    ) {
                        withSampleData = true;
                    }
                }
            }
        });
        trackProvisioningEventIfNeeded(isWizardVariation);
        return { isWizardVariation, withSampleData };
    };

    const onComplete = async (taskId?: string, onRedirect?: () => void) => {
        const productUrl = checkFeatureFlagGate(
            featureFlags.jiraOnboardingMultipleProjectRequestsBugfix
        )
            ? createRedirectUrl()
            : redirectUrl;

        if (productUrl) {
            await resolveBlockingRedirectPromises();

            if (
                checkFeatureFlagGate(
                    featureFlags.jiraOnboardingWizardMetricImprovements
                ) &&
                onRedirect
            ) {
                try {
                    onRedirect();
                } catch (error) {
                    // NO-OP, prevent redirect from failing
                }
            }

            createAnalyticsEvent({
                eventName: "redirectedToProduct",
                eventComponent: "confirmationPage",
                event: "redirected",
                eventType: "ui",
                action: "redirected",
                attributes: {
                    reactComponent: "JiraProvisioningWithOnboardingWizard"
                }
            }).fire();

            // SLO "good event" for redirect into product
            ufoEventRedirectToProduct.success({
                customData: {
                    analyticsEvent: "redirected",
                    redirectFrom: "confirmationPage",
                    reactComponent: "JiraProvisioningWithOnboardingWizard",
                    finalProvisioningScreen: "jira-onboarding-wizard"
                }
            });

            window.location.assign(
                createRedirectUrlWithWizard(productUrl, taskId)
            );
        }
    };

    const handleOnError = useCallback(
        (error: Error) => {
            createAnalyticsEvent({
                eventType: "operational",
                actionSubject: `${ANALYTICS_PREFIX}Screen`,
                action: "failed",
                attributes: {
                    error: error.message
                }
            }).fire();
        },
        [createAnalyticsEvent]
    );

    const { isWizardVariation, withSampleData } = getIsWizardVariation();

    // This acts like ProductRedirector to avoid showing JiraAnimatedProvisioningScreen if site is ready
    if (siteProvisionedOnMount && !isWizardVariation) {
        return null;
    }

    // Handles both cases where site is still provisioning and we show wizard
    // or show wizard immediately when site provisioned on mount
    if (isWizardVariation) {
        return (
            <ErrorBoundary
                fallback={<JiraAnimatedProvisioningScreen />}
                onError={handleOnError}
            >
                <WelcomeModal
                    onComplete={onComplete}
                    withSampleData={withSampleData}
                />
            </ErrorBoundary>
        );
    }

    return <JiraAnimatedProvisioningScreen />;
};
